import LoggerApi from '../api/LoggerApi';
import { useAuth } from '@presov.sk/presov-auth-react';
import React from 'react';
import { serializeError } from 'serialize-error';

export type LoggerPayload = {
  action: string;
  data?: unknown;
  error?: unknown;
};

type LoggerType = {
  info: (message: string, payload: LoggerPayload) => void;
  error: (message: string, payload: LoggerPayload) => void;
  warn: (message: string, payload: LoggerPayload) => void;
  debug: (message: string, payload: LoggerPayload) => void;
};

type LoggerContextType = {
  logger: LoggerType;
};

const LoggerContext = React.createContext<LoggerContextType>(undefined);

const LoggerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { authUser } = useAuth();

  const sendLog = (
    severity: 'INFO' | 'ERROR' | 'WARNING' | 'DEBUG',
    message: string,
    payload?: LoggerPayload,
  ) => {
    if (import.meta.env.PROD) {
      const { error, ...restPayload } = payload;

      LoggerApi.saveLog(undefined, {
        identifier: 'moj.presov.sk',
        severity,
        dataType: 'JSON' as const,
        clientAccId: authUser?.clientAccountId || null,
        data: {
          ...restPayload,
          message,
          errorPayload: error ? serializeError(error) : undefined,
        },
      });
    }

    const logMessage = `${severity}: ${message}`;
    if (severity === 'INFO') {
      console.info(logMessage, payload);
    } else if (severity === 'ERROR') {
      console.error(logMessage, payload);
    } else if (severity === 'WARNING') {
      console.warn(logMessage, payload);
    } else {
      console.debug(logMessage, payload);
    }
  };

  const logger: LoggerType = {
    info: (message: string, payload: LoggerPayload) =>
      sendLog('INFO', message, payload),
    error: (message: string, payload: LoggerPayload) =>
      sendLog('ERROR', message, payload),
    warn: (message: string, payload: LoggerPayload) =>
      sendLog('WARNING', message, payload),
    debug: (message: string, payload: LoggerPayload) =>
      sendLog('DEBUG', message, payload),
  };

  return (
    <LoggerContext.Provider
      value={{
        logger,
      }}
    >
      {children}
    </LoggerContext.Provider>
  );
};

export default LoggerProvider;

export const useLogger = () => {
  const context = React.useContext(LoggerContext);
  if (!context) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }
  return context;
};
